import React from "react";
import { Breadcrumb } from "antd";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePageCrumbValue } from "../../data/hooks/usePageCrumb";
import { findNavItemByPath, getNavCrumbGroups, hasNavCrumb, isNavDetailPath } from "../../functions/navigation";

/**
 * Breadcrumb in de vaste headerbalk, naast het meldingencentrum.
 *
 * Welk menu-item bij de huidige URL hoort komt uit functions/navigation.js —
 * dezelfde bron die de zijbalk gebruikt. Dat stond hier eerst apart, met een
 * eigen prefix-matching en een eigen aliastabel; sinds BMX-22 is dat één plek,
 * zodat kruimel en zijbalk niet uiteen kunnen lopen.
 *
 * Waarom prefix-matching en niet per padsegment:
 * - De eerste kruimel is een groepsnaam ("Inkoop") die niet in de URL voorkomt.
 * - Tussenliggende segmenten (/cms, /cms/purchase-orders) hebben geen titel en
 *   zouden lege, klikbare kruimels opleveren.
 *
 * De oude `AppBreadcrumb.js` deed het wél per segment en was daarnaast kapot
 * (`const { location } = useLocation()` — useLocation geeft de location zélf
 * terug, dus alles was undefined). Die is vervangen door dit bestand.
 *
 * Welke secties een kruimel krijgen staat in `CRUMB_SECTIONS` in
 * functions/navigation.js. Op alle andere routes geeft de component `null`
 * terug en blijft de headerbalk zoals hij was.
 */

const HeaderBreadcrumb = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const recordLabel = usePageCrumbValue();

  const item = findNavItemByPath(pathname);
  if (!item) return null;

  // Alleen de secties waarvoor een kruimel gevraagd is.
  if (!hasNavCrumb(pathname)) return null;

  const isDetail = isNavDetailPath(pathname);

  // Een item zonder href rendert in antd als platte tekst — precies de
  // niet-klikbare behandeling die het ontwerp voor de groep en het huidige
  // record laat zien. Geen eigen kleuren nodig.
  const items = [
    ...getNavCrumbGroups(pathname).map((group) => ({ key: group.key, title: t(group.title) })),
    isDetail ? { title: <Link to={item.path}>{t(item.title)}</Link> } : { title: t(item.title) },
    ...(isDetail ? [{ title: recordLabel || "…" }] : []),
  ];

  return (
    <Breadcrumb
      items={items}
      style={{
        fontSize: 13,
        // Truncatie zoals in het ontwerp: een lang recordnummer kapt zichzelf af
        // in plaats van de bel-knop uit beeld te duwen.
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: 0,
      }}
    />
  );
};

export default HeaderBreadcrumb;
